/**
 * Pending Absence Request — for approvers (managers).
 * SRS: managers see the team's attendance-correction requests AND the team's Pending Absence list
 * (to remind people), and approve or refer back with Single / Multiple / Select All.
 * Which employees an approver covers is decided by the backend routing rules.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { pendingAbsenceApi } from "../../utils/pendingAbsenceApi";
import LoadingIndicator from "../Common/components/LoadingIndicator";
import { ActionDialog, DateInput, DeadlineLabel, PageHeading, ShareButton, StatusBadge } from "./PendingAbsenceCommon";
import {
  displayStatus,
  formatDate,
  isAllSelected,
  itemKey,
  summarizeResults,
  toRequestItems,
  toggleAll,
  toggleKey,
} from "./pendingAbsenceHelpers";

const canDecide = (item) => item.can_approver_act;

export default function PendingAbsenceRequests() {
  const [tab, setTab] = useState("requests");
  const [range, setRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [decision, setDecision] = useState(null); // 'approve' | 'refer-back' | null
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await pendingAbsenceApi.get(
        tab === "requests" ? "/approver/requests" : "/approver/team",
        { from: range.from, to: range.to }
      );
      setData(result);
      setSelected(new Set());
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [tab, range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const items = (data && data.items) || [];
  const selectedItems = toRequestItems(items, selected);

  const submitDecision = async () => {
    if (!comment.trim()) return toast.error("Please enter a reason.");
    setBusy(true);
    try {
      const result = await pendingAbsenceApi.post(`/approver/${decision}`, {
        items: selectedItems,
        comment: comment.trim(),
      });
      const summary = summarizeResults(result);
      if (summary.succeeded > 0) {
        toast.success(
          `${summary.succeeded} request${summary.succeeded === 1 ? "" : "s"} ${
            decision === "approve" ? "approved" : "referred back"
          }.`
        );
      }
      summary.failures.forEach((message) => toast.error(message, { autoClose: 8000 }));
      setDecision(null);
      setComment("");
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const openDecision = (kind) => {
    setComment("");
    setDecision(kind);
  };

  return (
    <div>
      <ToastContainer position={toast.POSITION.TOP_RIGHT} />
      <PageHeading title="Pending Absence Request" />
      <div className="wrapper wrapper-content animated fadeInRight pending-absence-page">
        <div className="ibox float-e-margins">
          <div className="ibox-title pa-request-header">
            <ul className="nav nav-pills pa-request-tabs">
              <li className={tab === "requests" ? "active" : ""}>
                <a href="#requests" onClick={(event) => { event.preventDefault(); setTab("requests"); }}>
                  Correction Requests
                </a>
              </li>
              <li className={tab === "team" ? "active" : ""}>
                <a href="#team" onClick={(event) => { event.preventDefault(); setTab("team"); }}>
                  Team Pending Absences
                </a>
              </li>
            </ul>
            <div className="pa-request-dates">
              <label className="pa-request-date-field">From
                <DateInput
                  className="form-control input-sm"
                  value={range.from}
                  onChange={(value) => setRange({ ...range, from: value })}
                />
              </label>
              <label className="pa-request-date-field">To
                <DateInput
                  className="form-control input-sm"
                  value={range.to}
                  onChange={(value) => setRange({ ...range, to: value })}
                />
              </label>
            </div>
          </div>
          <div className="ibox-content">
            {loadError && <div className="alert alert-danger">{loadError}</div>}
            {loading && <LoadingIndicator backgroundColor="#ffffff" />}

            {!loading && data && !data.is_approver && (
              <p className="text-center text-muted" style={{ margin: 20 }}>
                You are not an approver for any employee.
              </p>
            )}

            {!loading && data && data.is_approver && tab === "requests" && (
              <div className="pa-card-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ marginRight: 8 }}
                  disabled={selectedItems.length === 0}
                  onClick={() => openDecision("approve")}
                >
                  Approve ({selectedItems.length})
                </button>
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  disabled={selectedItems.length === 0}
                  onClick={() => openDecision("refer-back")}
                >
                  Refer Back ({selectedItems.length})
                </button>
              </div>
            )}

            {!loading && data && data.is_approver && items.length === 0 && (
              <p className="text-center text-muted" style={{ margin: 20 }}>
                {tab === "requests"
                  ? "No correction requests are waiting for you."
                  : "Nobody in your team has a pending absence."}
              </p>
            )}

            {!loading && items.length > 0 && tab === "requests" && (
              <div className="table-responsive">
                <table className="table table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={isAllSelected(items, selected, canDecide)}
                          onChange={() => setSelected(toggleAll(items, selected, canDecide))}
                        />
                      </th>
                      <th>Employee</th>
                      <th>Designation</th>
                      <th>Branch</th>
                      <th>Absence Date</th>
                      <th>Reason</th>
                      <th>Deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={itemKey(item)}>
                        <td>
                          {canDecide(item) && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${item.display_name} ${item.absence_date}`}
                              checked={selected.has(itemKey(item))}
                              onChange={() => setSelected(toggleKey(selected, itemKey(item)))}
                            />
                          )}
                        </td>
                        <td>{item.display_name}</td>
                        <td>{item.designation}</td>
                        <td>{item.branch}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(item.absence_date)}</td>
                        <td>{item.request && item.request.reason}</td>
                        <td>
                          <DeadlineLabel item={item} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && items.length > 0 && tab === "team" && (
              <div className="table-responsive">
                <p className="text-muted">
                  Use this list to remind your team members to finish their corrections.
                </p>
                <table className="table table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Designation</th>
                      <th>Branch</th>
                      <th>Absence Date</th>
                      <th>Status</th>
                      <th>Deadline</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={itemKey(item)}>
                        <td>{item.display_name}</td>
                        <td>{item.designation}</td>
                        <td>{item.branch}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(item.absence_date)}</td>
                        <td>
                          <StatusBadge status={displayStatus(item)} />
                        </td>
                        <td>
                          <DeadlineLabel item={item} />
                        </td>
                        <td>
                          <ShareButton item={item} items={items} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ActionDialog
        visible={decision !== null}
        title={decision === "approve" ? "Approve correction requests" : "Refer back to employee"}
        confirmLabel={decision === "approve" ? "Approve" : "Refer Back"}
        confirmClassName={decision === "approve" ? "btn btn-primary" : "btn btn-warning"}
        onClose={() => setDecision(null)}
        onConfirm={submitDecision}
        busy={busy}
      >
        <p className="text-muted">
          {selectedItems.length} request{selectedItems.length === 1 ? "" : "s"} selected.
        </p>
        <div className="form-group">
          <label>Reason (required)</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </div>
      </ActionDialog>
    </div>
  );
}
